export const HUBUP_PARIS_TZ = 'Europe/Paris';

export function emptyNotificationCalendarEvent() {
  return {
    eventTitle: '',
    eventDesc: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '',
    specifyEnd: false,
  };
}

/** Date du jour à Paris (YYYY-MM-DD). */
export function minCalendarDateInputParis() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: HUBUP_PARIS_TZ });
}

/**
 * Convertit date (YYYY-MM-DD) + heure (HH:mm) interprétées à Paris → ISO UTC.
 */
export function parisDateTimeLocalToIso(dateStr, timeStr) {
  const date = String(dateStr || '').trim();
  const time = String(timeStr || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mi) || h > 23 || mi > 59) return null;
  const [y, mo, d] = date.split('-').map(Number);
  let candidate = Date.UTC(y, mo - 1, d, h, mi, 0);
  const fmt = new Intl.DateTimeFormat('sv-SE', {
    timeZone: HUBUP_PARIS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = Object.fromEntries(
      fmt.formatToParts(new Date(candidate)).map((p) => [p.type, p.value]),
    );
    const py = Number(parts.year);
    const pm = Number(parts.month);
    const pd = Number(parts.day);
    const ph = Number(parts.hour);
    const pmin = Number(parts.minute);
    if (py === y && pm === mo && pd === d && ph === h && pmin === mi) {
      return new Date(candidate).toISOString();
    }
    const got = Date.UTC(py, pm - 1, pd, ph, pmin, 0);
    const want = Date.UTC(y, mo - 1, d, h, mi, 0);
    candidate += want - got;
  }
  return new Date(candidate).toISOString();
}

export function isoToParisDateTimeParts(iso) {
  const d = new Date(String(iso || ''));
  if (Number.isNaN(d.getTime())) {
    return { date: '', time: '' };
  }
  const date = d.toLocaleDateString('sv-SE', { timeZone: HUBUP_PARIS_TZ });
  const time = d.toLocaleTimeString('sv-SE', {
    timeZone: HUBUP_PARIS_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return { date, time: time.slice(0, 5) };
}

export function calendarEventFromApi(raw) {
  if (raw == null || typeof raw !== 'object') return null;
  const eventTitle = String(raw.eventTitle ?? '').trim();
  if (!eventTitle) return null;
  const startsAt = String(raw.startsAt ?? '').trim();
  if (!startsAt) return null;
  const startParts = isoToParisDateTimeParts(startsAt);
  const endsAtRaw = String(raw.endsAt ?? '').trim();
  const endParts = endsAtRaw ? isoToParisDateTimeParts(endsAtRaw) : { date: '', time: '' };
  return {
    eventTitle,
    eventDesc: String(raw.eventDesc ?? ''),
    startDate: startParts.date,
    startTime: startParts.time || '09:00',
    endDate: endParts.date,
    endTime: endParts.time,
    specifyEnd: Boolean(endsAtRaw),
  };
}

export function validateCalendarEventDraft(draft) {
  if (!draft) return null;
  const eventTitle = String(draft.eventTitle ?? '').trim();
  if (!eventTitle) return 'Le titre de l’événement est requis.';
  const startDate = String(draft.startDate ?? '').trim();
  const startTime = String(draft.startTime ?? '').trim();
  if (!startDate || !startTime) {
    return 'Indiquez la date et l’heure de début (Europe/Paris).';
  }
  const startsAt = parisDateTimeLocalToIso(startDate, startTime);
  if (!startsAt) return 'Date ou heure de début invalide.';
  if (draft.specifyEnd) {
    const endDate = String(draft.endDate ?? '').trim();
    const endTime = String(draft.endTime ?? '').trim();
    if (!endDate || !endTime) {
      return 'Indiquez la date et l’heure de fin, ou désactivez la fin personnalisée.';
    }
    const endsAt = parisDateTimeLocalToIso(endDate, endTime);
    if (!endsAt) return 'Date ou heure de fin invalide.';
    if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      return 'La fin doit être après le début.';
    }
  }
  return null;
}

export function serializeCalendarEvent(draft) {
  const eventTitle = String(draft.eventTitle ?? '').trim();
  const startsAt = parisDateTimeLocalToIso(
    draft.startDate,
    draft.startTime,
  );
  if (!eventTitle || !startsAt) return null;
  const out = {
    eventTitle,
    startsAt,
  };
  const desc = String(draft.eventDesc ?? '').trim();
  if (desc) out.eventDesc = desc;
  if (draft.specifyEnd) {
    const endsAt = parisDateTimeLocalToIso(draft.endDate, draft.endTime);
    if (endsAt) out.endsAt = endsAt;
  }
  return out;
}

export function buildNotificationCalendarEventPayload(
  eventEnabled,
  linkedEvent,
  hadEventBefore,
) {
  if (!eventEnabled || !linkedEvent) {
    if (hadEventBefore) return { ok: true, payload: { calendarEvent: null } };
    return { ok: true, payload: {} };
  }
  const serialized = serializeCalendarEvent(linkedEvent);
  if (!serialized) {
    return {
      ok: false,
      error:
        'L’événement est incomplet. Vérifiez le titre et le début, puis validez.',
    };
  }
  return { ok: true, payload: { calendarEvent: serialized } };
}

export function resolveNotificationCalendarEventPayload(
  eventEnabled,
  linkedEvent,
  hadEventBefore,
) {
  const built = buildNotificationCalendarEventPayload(
    eventEnabled,
    linkedEvent,
    hadEventBefore,
  );
  if (!built.ok) return { error: built.error };
  return { payload: built.payload };
}

export function formatCalendarEventPreviewLine(draft) {
  if (!draft?.eventTitle?.trim()) return '';
  const start = parisDateTimeLocalToIso(draft.startDate, draft.startTime);
  if (!start) return draft.eventTitle.trim();
  const startLabel = new Date(start).toLocaleString('fr-FR', {
    timeZone: HUBUP_PARIS_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${draft.eventTitle.trim()} · ${startLabel}`;
}
